import { useState } from 'react';
import { corEtiqueta, iniciaisDe, quebrarPorQue } from '../montagem';
import type { EquipePlacar, PessoaMontagem } from '../types';
import SeletorAlocar from './SeletorAlocar';

interface Props {
  pessoa: PessoaMontagem;
  equipeId: number | null;
  equipes: EquipePlacar[];
  fotoUrl: string | null;
  aoAlocar: (p: PessoaMontagem) => void;
  aoDesalocar: (p: PessoaMontagem) => void;
  aoEscolherEquipe: (p: PessoaMontagem, equipeId: number) => void;
  aoAbrirFicha: () => void;
}

const CLASSE_ETIQUETA: Record<string, string> = {
  verde: 'bg-green-100 text-green-800',
  ambar: 'bg-amber-100 text-amber-800',
  vermelha: 'bg-red-100 text-red-700',
  cinza: 'bg-secondary-100 text-secondary-700',
};

function Avatar({ src, nome }: { src: string | null; nome: string }) {
  if (src) {
    return (
      <img
        src={src}
        alt={nome}
        loading="lazy"
        className="h-10 w-10 shrink-0 rounded-full border border-secondary-200 object-cover"
      />
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-xs font-semibold text-secondary-600">
      {iniciaisDe(nome)}
    </div>
  );
}

export default function CartaoPessoa({
  pessoa,
  equipeId,
  equipes,
  fotoUrl,
  aoAlocar,
  aoDesalocar,
  aoEscolherEquipe,
  aoAbrirFicha,
}: Props) {
  const [talentoExpandido, setTalentoExpandido] = useState(false);

  const naEstaEquipe = equipeId !== null && pessoa.equipe_id === equipeId;
  const emOutraEquipe = pessoa.equipe_id !== null && !naEstaEquipe;
  const confirmada = naEstaEquipe && pessoa.status_alocacao === 'confirmada';

  const etiquetas = quebrarPorQue(pessoa.por_que);

  const idade = pessoa.idade !== null ? `${pessoa.idade} anos` : '';
  const cidade = pessoa.cidade ? `· ${pessoa.cidade}` : '';
  const subtitulo = [idade, cidade].filter(Boolean).join(' ');

  const sub = [pessoa.paroquia, pessoa.profissao || pessoa.trabalho].filter(Boolean).join(' · ');

  const talentosLongos = (pessoa.talentos || '').trim().length > 80;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={aoAbrirFicha}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          aoAbrirFicha();
        }
      }}
      className={`cursor-pointer rounded-3xl p-4 transition-colors ${
        naEstaEquipe
          ? 'bg-primary-50 ring-2 ring-primary-300'
          : 'bg-white shadow-[0_6px_18px_rgba(20,25,60,.06)] hover:bg-primary-50'
      } ${pessoa.recusou ? 'opacity-50' : ''}`}
    >
      <div className="flex items-start gap-3">
        <Avatar src={fotoUrl} nome={pessoa.nome} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-semibold uppercase text-foreground-950">
                  {pessoa.nome}
                </p>
                {confirmada && (
                  <span className="shrink-0 whitespace-nowrap rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    confirmada
                  </span>
                )}
              </div>
              {subtitulo && <p className="text-xs text-secondary-500">{subtitulo}</p>}
            </div>

            <div
              className="flex shrink-0 items-center gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              {equipeId !== null ? (
                naEstaEquipe ? (
                  <button
                    type="button"
                    onClick={() => aoDesalocar(pessoa)}
                    title="Tirar desta equipe"
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-secondary-300 bg-white text-secondary-600 transition-colors hover:bg-secondary-100"
                  >
                    <i className="ri-subtract-line text-base" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => aoAlocar(pessoa)}
                    title="Colocar nesta equipe"
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-primary-500 text-white transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-add-line text-base" />
                  </button>
                )
              ) : (
                <>
                  <SeletorAlocar
                    jaEstaEmEquipe={emOutraEquipe}
                    equipes={equipes}
                    aoEscolher={(id) => aoEscolherEquipe(pessoa, id)}
                  />
                  {emOutraEquipe && (
                    <button
                      type="button"
                      onClick={() => aoDesalocar(pessoa)}
                      title="Tirar da equipe atual"
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-secondary-300 bg-white text-secondary-600 transition-colors hover:bg-secondary-100"
                    >
                      <i className="ri-subtract-line text-base" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {sub && <p className="mt-1 text-xs text-secondary-600">{sub}</p>}

          {pessoa.ja_serviu_aqui && (
            <p className="mt-1 flex items-center gap-1 text-xs font-medium text-green-700">
              <i className="ri-check-line" />
              já serviu nesta equipe
            </p>
          )}

          {etiquetas.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {etiquetas.map((e) => (
                <span
                  key={e}
                  className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${CLASSE_ETIQUETA[corEtiqueta(e)]}`}
                >
                  {e}
                </span>
              ))}
            </div>
          )}

          {pessoa.talentos && (
            <div className="mt-2">
              <p
                className={`text-xs italic text-secondary-700 ${
                  talentoExpandido ? '' : 'line-clamp-2'
                }`}
              >
                {pessoa.talentos}
              </p>
              {talentosLongos && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTalentoExpandido((v) => !v);
                  }}
                  className="mt-0.5 cursor-pointer whitespace-nowrap text-xs font-medium text-primary-600 hover:underline"
                >
                  {talentoExpandido ? 'ver menos' : 'ver mais'}
                </button>
              )}
            </div>
          )}

          {emOutraEquipe && pessoa.equipe_atual && (
            <p className="mt-2 text-xs text-secondary-500">
              → hoje em:{' '}
              <span className="font-medium text-foreground-800">{pessoa.equipe_atual}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}